import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LuBookOpen, LuCheckCircle2, LuLoader2, LuWand2, LuWrench } from "react-icons/lu";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import { useAppInfo } from "../../../helpers/hooks/common-hook";
import {
	CATEGORY_NAMES,
	STATUS,
	generatePlan,
	getPlan,
	getRun,
	isTerminal,
	latestResults,
	listPlans,
	listRuns,
	pollPlan,
	pollRun,
	startRun,
	summarize,
} from "../../../services/agent-eval";
import { PlanCoverage } from "./scenario-details";
import ScenarioList, { FILTERS } from "./scenario-list";

/**
 * Agent Tests.
 *
 * Built for someone who does not know how to test an agent. One button reads
 * everything the agent is connected to — instructions, workflows, skills,
 * tools, MCP servers, sub-agents and knowledge — and writes customer
 * conversations for each. One more runs them. What is left on the page is the
 * result: what passed, what did not, and why, in plain words.
 */

const scenariosOf = (plan) =>
	(plan?.categories || []).flatMap((category) => (category.cases || []).map((c) => ({ ...c, category: category.id })));

function mergeCases(results, cases) {
	const next = { ...results };
	for (const result of cases || []) {
		if (result.status !== STATUS.CANCELLED || !next[result.caseId]) next[result.caseId] = result;
	}
	return next;
}

// ─── Empty and in-progress states ─────────────────────────────────────────────

const STEPS = [
	[LuBookOpen, "We read your agent", "Its instructions, workflows, skills, tools, MCP servers and knowledge."],
	[LuWand2, "We write the tests", "Real customer conversations for each part — up to 10 per skill, tool or workflow and up to 50 questions about your knowledge."],
	[LuCheckCircle2, "You see what works", "Run them against your live agent and see what passed, what did not, and why."],
];

function Welcome({ onCreate, creating }) {
	return (
		<div className="max-w-2xl mx-auto py-10 text-center">
			<h2 className="text-xl font-semibold text-gray-800">Check that your agent works — no setup needed</h2>
			<p className="text-sm text-gray-500 mt-2">We work out what to test from how your agent is built.</p>
			<div className="grid gap-3 sm:grid-cols-3 mt-6 text-left">
				{STEPS.map(([Icon, title, body]) => (
					<div key={title} className="rounded-xl border border-gray-200 p-4">
						<Icon className="h-5 w-5 text-primary mb-2" />
						<p className="text-sm font-medium text-gray-800">{title}</p>
						<p className="text-xs text-gray-500 mt-1">{body}</p>
					</div>
				))}
			</div>
			<div className="mt-6 flex justify-center">
				<Button text="Create test scenarios" loading={creating} onClick={onCreate} />
			</div>
		</div>
	);
}

function Creating() {
	return (
		<div role="status" className="max-w-xl mx-auto py-14 text-center">
			<LuLoader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
			<p className="text-gray-800 font-medium mt-4">Creating test scenarios…</p>
			<p className="text-sm text-gray-500 mt-1">
				Reading your agent's instructions, skills, tools, workflows and knowledge, and writing conversations to test
				each one. This usually takes a few minutes — you can leave this page and come back.
			</p>
		</div>
	);
}

// ─── Summary ──────────────────────────────────────────────────────────────────

function Summary({ plan, results, activeRun, lastRunAt }) {
	const scenarios = scenariosOf(plan);
	const counts = summarize(scenarios, results);
	const running = activeRun && !isTerminal(activeRun.status);
	const progress = activeRun?.summary || {};
	const tone = counts.score == null ? "text-gray-800"
		: counts.score >= 90 ? "text-green-600" : counts.score >= 70 ? "text-amber-600" : "text-red-600";

	const areas = (plan?.categories || []).map((category) => {
		const ids = (category.cases || []).map((c) => c.id);
		const ran = ids.filter((id) => results[id] && results[id].status !== STATUS.CANCELLED);
		return { id: category.id, total: ids.length, ran: ran.length,
			passed: ran.filter((id) => results[id].status === STATUS.PASSED).length };
	});

	return (
		<div className="rounded-xl border border-gray-200 bg-white p-5">
			<div className="flex flex-wrap items-start gap-6">
				<div className="min-w-[180px]">
					{counts.score == null ? (
						<>
							<p className="text-2xl font-semibold text-gray-800">{counts.total} scenarios</p>
							<p className="text-sm text-gray-500">ready to run</p>
						</>
					) : (
						<>
							<p className={`text-4xl font-semibold ${tone}`}>{counts.score}%</p>
							<p className="text-sm text-gray-600">{counts.passed} of {counts.passed + counts.failed} passed</p>
						</>
					)}
					<p className="text-xs text-gray-500 mt-1">
						{[
							counts.failed && `${counts.failed} need attention`,
							counts.errored && `${counts.errored} could not run`,
							counts.score != null && counts.notRun && `${counts.notRun} not run yet`,
						].filter(Boolean).join(" · ")}
					</p>
					{lastRunAt && !running && (
						<p className="text-xs text-gray-400 mt-1">Last run {new Date(lastRunAt).toLocaleString()}</p>
					)}
				</div>

				<div className="flex-1 min-w-[240px] grid gap-2 sm:grid-cols-2">
					{areas.map((area) => (
						<div key={area.id}>
							<div className="flex justify-between text-xs text-gray-600">
								<span>{CATEGORY_NAMES[area.id] || area.id}</span>
								<span>{area.ran ? `${area.passed}/${area.ran}` : `${area.total} ready`}</span>
							</div>
							<div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1 flex">
								<div className="h-full bg-green-500" style={{ width: `${area.total ? (area.passed / area.total) * 100 : 0}%` }} />
								<div className="h-full bg-red-400" style={{ width: `${area.total ? ((area.ran - area.passed) / area.total) * 100 : 0}%` }} />
							</div>
						</div>
					))}
				</div>
			</div>

			{running && (
				<div className="mt-4" role="status">
					<div className="flex justify-between text-xs mb-1">
						<span className="text-blue-600 inline-flex items-center gap-1.5">
							<LuLoader2 className="h-3.5 w-3.5 animate-spin" />
							Testing… {progress.completed || 0} of {progress.total || 0} done
						</span>
						<span className="text-gray-500">Results appear as each conversation finishes.</span>
					</div>
					<div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
						<div className="h-full bg-blue-500 transition-all duration-500"
							style={{ width: `${progress.total ? ((progress.completed || 0) / progress.total) * 100 : 0}%` }} />
					</div>
				</div>
			)}
			{activeRun?.error && <p className="text-sm text-amber-700 mt-3">{activeRun.error}</p>}
		</div>
	);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AgentEval() {
	const appInfo = useAppInfo();
	const appId = appInfo?.appId;
	const [searchParams] = useSearchParams();
	const requestedPlanId = searchParams.get("planId");
	const requestedRunId = searchParams.get("runId");

	const [loading, setLoading] = useState(true);
	const [plan, setPlan] = useState(null);
	const [results, setResults] = useState({});
	const [activeRun, setActiveRun] = useState(null);
	const [lastRunAt, setLastRunAt] = useState(null);
	const [creating, setCreating] = useState(false);
	const [filter, setFilter] = useState("all");
	const [toast, setToast] = useState(null);

	const pollController = useRef(null);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			pollController.current?.abort();
		};
	}, []);

	const notify = (text, type = "success") => setToast({ text, type, key: Date.now() });
	const running = !!activeRun && !isTerminal(activeRun.status);

	/** Follow a run, folding each finished conversation into the results. */
	const follow = useCallback(async (runId) => {
		pollController.current?.abort();
		const controller = new AbortController();
		pollController.current = controller;
		const finished = await pollRun(appId, runId, (header) => {
			if (!mounted.current) return;
			setActiveRun(header);
			if (header.cases) setResults((prev) => mergeCases(prev, header.cases));
		}, { signal: controller.signal, includeCases: true });
		if (!mounted.current || controller.signal.aborted || !finished) return;
		setActiveRun(finished);
		setResults((prev) => mergeCases(prev, finished.cases));
		if (!isTerminal(finished.status)) {
			notify("Tests are still running. Come back to this page to see the results.", "warning");
			return;
		}
		setLastRunAt(finished.startedAt);
		const summary = finished.summary || {};
		notify(`Testing finished — ${summary.passed || 0} of ${summary.evaluated || 0} passed.`,
			finished.status === STATUS.PASSED ? "success" : "warning");
	}, [appId]);

	const load = useCallback(async (signal) => {
		if (!appId) return;
		setLoading(true);
		setPlan(null); setResults({}); setActiveRun(null);

		const [planHeaders, runHeaders] = await Promise.all([listPlans(appId), listRuns(appId)]);
		if (signal?.aborted) return;

		const requestedRun = requestedRunId ? runHeaders.find((r) => r.id === requestedRunId)
			|| await getRun(appId, requestedRunId, false) : null;
		const planId = requestedPlanId || requestedRun?.planId || planHeaders[0]?.id;
		const current = planId ? await getPlan(appId, planId) : null;
		if (signal?.aborted) return;
		setPlan(current);

		if (current) {
			const merged = await latestResults(appId, current.id, runHeaders);
			if (signal?.aborted) return;
			setResults(merged);
			const ofPlan = runHeaders.filter((r) => r.planId === current.id);
			setLastRunAt(ofPlan.find((r) => isTerminal(r.status))?.startedAt || null);
			const inFlight = requestedRun && !isTerminal(requestedRun.status) ? requestedRun
				: ofPlan.find((r) => !isTerminal(r.status));
			if (inFlight) {
				setActiveRun(inFlight);
				follow(inFlight.id);
			}
		}
		setLoading(false);
	}, [appId, follow, requestedPlanId, requestedRunId]);

	useEffect(() => {
		const controller = new AbortController();
		load(controller.signal);
		return () => { controller.abort(); pollController.current?.abort(); };
	}, [load]);

	// Scenario creation runs in the background; follow it until it is ready.
	useEffect(() => {
		if (!appId || !plan?.id || plan.generationStatus !== "generating") return undefined;
		const controller = new AbortController();
		pollPlan(appId, plan.id, (value) => setPlan(value), { signal: controller.signal })
			.then((ready) => {
				if (ready?.generationStatus === "ready" && !controller.signal.aborted) {
					const count = scenariosOf(ready).length;
					notify(`${count} test scenario${count === 1 ? "" : "s"} ready. Run them to see how your agent does.`);
				}
			})
			.catch((error) => { if (!controller.signal.aborted) notify(error.message, "warning"); });
		return () => controller.abort();
	}, [appId, plan?.id, plan?.generationStatus]);

	const handleCreate = async () => {
		setCreating(true);
		try {
			const { response, status } = await generatePlan(appId, plan?.id);
			if (status && status !== 200) {
				notify(typeof response === "string" ? response : "Could not start creating test scenarios.", "failure");
				return;
			}
			setPlan(response);
			setResults({});
			setLastRunAt(null);
			setFilter("all");
		} finally {
			setCreating(false);
		}
	};

	const handleRun = async (caseIds) => {
		if (!plan || running) return;
		const { response, status } = await startRun(appId, { planId: plan.id, caseIds });
		if (![200, 202].includes(status) || !(response?.runId || response?.id)) {
			notify(typeof response === "string" ? response : "Could not start the tests. Try again.", "failure");
			return;
		}
		const runId = response.runId || response.id;
		setActiveRun({ ...response, id: runId });
		follow(runId);
	};

	const generating = plan?.generationStatus === "generating";
	const ready = plan && !generating && scenariosOf(plan).length > 0;

	const headerAction = ready && (
		<div className="flex items-center gap-2">
			<Button text="Recreate scenarios" color="default" disabled={running || creating} onClick={handleCreate} />
			<Button text={running ? "Testing…" : "Run all tests"} loading={running} onClick={() => handleRun()} />
		</div>
	);

	return (
		<PageContainer
			title="Agent Tests"
			subtitle="Automatic tests for everything your agent is connected to."
			headerAction={headerAction}
		>
			{toast && <Message key={toast.key} text={toast.text} type={toast.type} />}

			<div className="pb-8 space-y-5">
				{loading ? (
					<p className="text-sm text-gray-400 py-12 text-center">Loading…</p>
				) : !plan ? (
					<Welcome onCreate={handleCreate} creating={creating} />
				) : generating ? (
					<Creating />
				) : !ready ? (
					<div className="max-w-xl mx-auto py-10 text-center space-y-4">
						<LuWrench className="h-8 w-8 text-gray-300 mx-auto" />
						<PlanCoverage plan={plan} />
						{!plan.generationError && !(plan.coverageWarnings || []).length && (
							<p className="text-sm text-gray-500">No test scenarios could be created for this agent.</p>
						)}
						<div className="flex justify-center">
							<Button text="Try again" loading={creating} onClick={handleCreate} />
						</div>
					</div>
				) : (
					<>
						<Summary plan={plan} results={results} activeRun={activeRun} lastRunAt={lastRunAt} />
						<PlanCoverage plan={plan} compact />
						<div className="flex flex-wrap items-center gap-1 border-b border-gray-200">
							{FILTERS.map(([id, label]) => (
								<button
									key={id}
									type="button"
									onClick={() => setFilter(id)}
									className={`px-3 py-2 text-sm border-b-2 -mb-px ${filter === id
										? "border-primary text-primary font-medium"
										: "border-transparent text-gray-500 hover:text-gray-700"}`}
								>
									{label}
								</button>
							))}
							<span className="ml-auto text-xs text-gray-400 pb-1">Tests talk to your live agent and may use its connected tools.</span>
						</div>
						<ScenarioList plan={plan} results={results} filter={filter} onRun={handleRun} running={running} />
					</>
				)}
			</div>
		</PageContainer>
	);
}
